import React, { useState, useEffect } from 'react';
import { domainService } from '../../services/domainService';
import { DomainInfo } from '../../types/domain';
import { SubdomainSettings } from './SubdomainSettings';
import { CustomDomainSettings } from './CustomDomainSettings';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Globe } from 'lucide-react';

export const DomainsSection: React.FC = () => {
  const { toast } = useToast();
  const [domainInfo, setDomainInfo] = useState<DomainInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDomainInfo = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const info = await domainService.getDomainInfo();
      setDomainInfo(info);
    } catch (err: any) {
      console.error('Error loading domain info:', err);
      setError(err.message || 'Failed to load domain information');
      toast({
        title: 'Error',
        description: err.message || 'Failed to load domain information',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDomainInfo();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !domainInfo) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
          <Globe className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Failed to Load Domain Settings</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {error || 'An unexpected error occurred'}
        </p>
        <button
          onClick={loadDomainInfo}
          className="text-sm text-primary hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Domains</h2>
        <p className="text-muted-foreground mt-1">
          Configure your workspace subdomain and custom domain settings
        </p>
      </div>

      {/* Subdomain Settings */}
      <SubdomainSettings
        currentSubdomain={domainInfo.subdomain}
        baseDomain={import.meta.env.VITE_BASE_DOMAIN || 'kynection.com'}
        onSubdomainUpdated={loadDomainInfo}
      />

      {/* Custom Domain Settings */}
      <CustomDomainSettings
        customDomain={domainInfo.customDomain}
        isEnabled={domainInfo.isCustomDomainEnabled}
        onDomainUpdated={loadDomainInfo}
      />

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-sm mb-2">🌐 About Domains</h4>
        <div className="text-xs text-muted-foreground space-y-2">
          <p>
            <strong>Subdomain:</strong> A subdomain under our main domain (e.g., acme.kynection.com).
            This is the easiest way to access your workspace and is configured automatically.
          </p>
          <p>
            <strong>Custom Domain:</strong> Use your own domain name (e.g., crm.yourcompany.com).
            This requires DNS configuration with your domain provider and SSL certificate setup.
          </p>
          <p>
            Both options can be used simultaneously. Your team can access the workspace using either URL.
          </p>
        </div>
      </div>
    </div>
  );
};
